/**
 * Generic event tracking utility used by all domain services.
 * 
 * This module provides the core event tracking API that domain services
 * (auth, leads, search, etc.) use to send events. It handles:
 * - Auto-injection of standard attributes (tenant, timestamp, etc.)
 * - Session ID generation for journey tracking
 * - Routing to the event dispatcher
 */

import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';
import { isClientSide } from 'utils/host-config';
import { getObservabilityConfig } from '../config';
import { dispatchEvent } from '../transport/event-dispatcher';
import { StandardEventAttributes } from '../providers/types';
import { getDeviceIdFromCookie } from 'utils/device-id';
import { getJSON } from 'utils/cookies';
import { USER_COOKIE_NAME } from 'constants/cookies';
import configJSON from 'config.json';

function getTenantId(providedTenantId?: string): string {
  if (providedTenantId) {
    return providedTenantId;
  }
  
  return 'unknown';
}

function getEnvironment(): string {
  return configJSON.environment || 'unknown';
}

function getUserId(): string | undefined {
  if (!isClientSide()) {
    return undefined;
  }
  
  try {
    const userCookie = Cookies.get(USER_COOKIE_NAME);
    if (userCookie) {
      const user = getJSON<{ id: string | number }>(userCookie);
      return user?.id?.toString();
    }
  } catch {
    // Silently fail - observability should never break the app
  }
  
  return undefined;
}

function getDeviceId(): string | undefined {
  if (isClientSide()) {
    return getDeviceIdFromCookie();
  }
  return undefined;
}

function buildStandardAttributes(tenantId?: string): StandardEventAttributes {
  return {
    tenantId: getTenantId(tenantId),
    timestamp: Date.now(),
    environment: getEnvironment(),
    userId: getUserId(),
    deviceId: getDeviceId(),
  };
}

/**
 * Start a new user journey and track the initial event.
 * 
 * This generates a unique session ID that should be passed to subsequent
 * trackEvent calls to link related events together.
 * 
 * @param eventName - The name of the journey start event (e.g., 'auth_signin_attempt')
 * @param customAttributes - Domain-specific attributes to include
 * @param tenantId - Optional tenant ID from component context (recommended)
 * @returns A unique session ID for tracking this journey
 */
export function startJourney(
  eventName: string,
  customAttributes?: Record<string, unknown>,
  tenantId?: string
): string {
  const sessionId = uuid();
  trackEvent(eventName, { ...customAttributes, sessionId }, tenantId);
  return sessionId;
}

/**
 * Track an observability event with auto-injected standard attributes.
 * 
 * This is the core tracking function used by all domain services. It:
 * - Auto-injects standard attributes (tenant, timestamp, etc.)
 * - Merges custom attributes with standard attributes
 * - Dispatches the event to the active provider
 * 
 * The method never throws errors - all failures are handled gracefully.
 * 
 * @param eventName - The name of the event to track (e.g., 'auth_signin_success')
 * @param customAttributes - Domain-specific attributes to include (should include sessionId if part of a journey)
 * @param tenantId - Optional tenant ID from component context (recommended)
 */
export function trackEvent(
  eventName: string,
  customAttributes?: Record<string, unknown>,
  tenantId?: string
): void {
  const config = getObservabilityConfig();

  try {
    const standardAttributes = buildStandardAttributes(tenantId);
    const allAttributes = {
      ...standardAttributes,
      ...customAttributes,
    };

    if (config.enableDevMode) {
      console.log(
        `%c[Event Tracker]%c ${eventName}`,
        'color: #10b981; font-weight: bold',
        'color: inherit',
        allAttributes
      );
    }

    dispatchEvent(eventName, allAttributes);
  } catch (error) {
    console.error('[Observability] Failed to track event:', eventName, error);
  }
}

